import {
  useId,
  type ElementType,
  type HTMLAttributes,
  type FormEventHandler,
  type PointerEventHandler,
  type ReactEventHandler,
  type ReactNode,
} from "react";
import "./Window.scss";

export type WindowProps = Omit<HTMLAttributes<HTMLElement>, "onSubmit"> & {
  as?: ElementType;
  children?: ReactNode;
  closedby?: "any" | "closerequest" | "none";
  eyebrow?: ReactNode;
  modal?: boolean;
  onClose: () => void;
  onCancel?: ReactEventHandler<HTMLDialogElement>;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  onTopbarPointerDown?: PointerEventHandler<HTMLElement>;
  open?: boolean;
  subtitle?: ReactNode;
  title: ReactNode;
};

export function Window({
  as: Component = "section",
  children,
  closedby,
  className = "",
  eyebrow,
  modal = true,
  onClose,
  onCancel,
  onTopbarPointerDown,
  open,
  subtitle,
  title,
  ...props
}: WindowProps) {
  const titleId = useId();

  return (
    <Component
      aria-labelledby={titleId}
      aria-modal={modal ? "true" : undefined}
      className={`window ${className}`.trim()}
      closedby={closedby}
      onCancel={onCancel}
      open={open}
      role={Component === "section" ? "dialog" : undefined}
      {...props}
    >
      <header className="window-topbar" onPointerDown={onTopbarPointerDown} role="presentation">
        <div className="window-heading">
          {eyebrow && <p className="window-eyebrow">{eyebrow}</p>}
          <h2 className="window-title" id={titleId}>
            {title}
          </h2>
          {subtitle && <p className="window-subtitle">{subtitle}</p>}
        </div>
        <button
          aria-label={`Close ${typeof title === "string" ? title : "window"}`}
          className="window-close"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="window-body">{children}</div>
    </Component>
  );
}
